import { useEffect, useRef } from "react";

import { Loader } from "@orgmym/leeloo-core";

import * as styles from "./infinite-scroll.css";

interface InfiniteScrollProps {
  children: React.ReactNode;
  hasMore: boolean;
  loadMore: () => void;
  isLoading: boolean;
  className?: string;
}

const InfiniteScroll = ({
  children,
  hasMore,
  loadMore,
  isLoading,
  className,
  ...props
}: InfiniteScrollProps) => {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoading && hasMore) {
          loadMore();
        }
      },
      { threshold: 0 }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [isLoading, hasMore, loadMore]);

  return (
    <div data-testid="infinite-scroll" className={className} {...props}>
      {children}

      {isLoading && (
        <Loader
          spin={true}
          className={styles.loader}
          data-testid="infinite-scroll-loader"
        />
      )}

      <div ref={sentinelRef} />
    </div>
  );
};

export default InfiniteScroll;
